import React, { useState, useEffect } from 'react';
import { Save, Upload, Image as ImageIcon, Download, X } from 'lucide-react';
import axios from 'axios';
import { useSiteSettings } from '../contexts/SiteSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IMAGE_FIELDS = [
  { key: 'logo', label: 'Logo (Navbar & Footer)', hint: 'PNG/SVG avec fond transparent, 256×256 recommandé' },
  { key: 'heroImage', label: "Image d'accueil (Hero)", hint: 'JPG/PNG 1920×1080 recommandé' },
  { key: 'aboutImage1', label: 'À propos - Image "Notre histoire"', hint: 'JPG/PNG 1200×800 recommandé' },
  { key: 'aboutImage2', label: 'À propos - Image "Ce qui nous différencie"', hint: 'JPG/PNG 1200×800 recommandé' },
  { key: 'servicesImage', label: 'Services - Image "Support technique"', hint: 'JPG/PNG 1200×800 recommandé' },
  { key: 'occasionsHeroImage', label: 'Occasions - Image atelier', hint: 'JPG/PNG 1200×800 recommandé' },
];

const MAX_SIZE_MB = 3;

const SiteSettings = () => {
  const { refetch } = useSiteSettings();
  const [settings, setSettings] = useState({
    logo: '',
    heroImage: '',
    heroTitle: 'Machines Laser',
    heroSubtitle: 'Professionnelles',
    aboutImage1: '',
    aboutImage2: '',
    servicesImage: '',
    occasionsHeroImage: '',
    contactPhone: '07 61 20 33 70',
    contactAddress: "465 route de l'oasis, Moidieu",
    contactEmail: 'contact@akmlaser.com',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`);
      if (response.data) {
        setSettings((prev) => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleImageUpload = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Le fichier doit être une image.');
      return;
    }

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      alert(`L'image ne doit pas dépasser ${MAX_SIZE_MB} MB.`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSettings((prev) => ({ ...prev, [field]: reader.result }));
    };
    reader.readAsDataURL(file);
    // reset input pour pouvoir re-uploader la même image
    e.target.value = '';
  };

  const handleClearImage = (field) => {
    setSettings((prev) => ({ ...prev, [field]: '' }));
  };

  const handleDownloadImage = (field, label) => {
    const url = settings[field];
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${field}.png`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API_URL}/api/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage('Paramètres sauvegardés avec succès.');
      // refresh global context so images are immediately visible on the site
      if (refetch) refetch();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Erreur lors de la sauvegarde.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="site-settings-page">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-6" style={{ color: '#5C3A21' }}>
          Paramètres du site
        </h2>

        {message && (
          <div
            data-testid="settings-message"
            className={`p-4 rounded-lg mb-6 ${
              message.includes('succès') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}
          >
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Images Section - Universelle */}
          <div className="border-b pb-6">
            <h3 className="text-xl font-semibold mb-4" style={{ color: '#5C3A21' }}>
              Images du site
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Chaque image peut être modifiée ou téléchargée. Les images sont converties en Base64 et stockées dans la base de données.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {IMAGE_FIELDS.map((fld) => (
                <div key={fld.key} className="border rounded-lg p-4 bg-gray-50" data-testid={`image-block-${fld.key}`}>
                  <label className="block text-sm font-semibold mb-2">{fld.label}</label>

                  <div className="border-2 border-dashed rounded-lg p-4 text-center bg-white">
                    {settings[fld.key] ? (
                      <div className="relative">
                        <img
                          src={settings[fld.key]}
                          alt={fld.label}
                          className="max-h-40 mx-auto mb-3 rounded object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => handleClearImage(fld.key)}
                          className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow"
                          title="Supprimer l'image"
                          data-testid={`clear-image-${fld.key}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <ImageIcon className="w-16 h-16 mx-auto text-gray-400 mb-2" />
                    )}

                    <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, fld.key)}
                        className="hidden"
                        id={`upload-${fld.key}`}
                      />
                      <label
                        htmlFor={`upload-${fld.key}`}
                        className="cursor-pointer inline-flex items-center space-x-2 px-3 py-2 rounded-lg text-white text-sm font-semibold"
                        style={{ backgroundColor: '#5C3A21' }}
                        data-testid={`upload-btn-${fld.key}`}
                      >
                        <Upload className="w-4 h-4" />
                        <span>Changer</span>
                      </label>

                      {settings[fld.key] && (
                        <button
                          type="button"
                          onClick={() => handleDownloadImage(fld.key, fld.label)}
                          className="inline-flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-300"
                          data-testid={`download-btn-${fld.key}`}
                        >
                          <Download className="w-4 h-4" />
                          <span>Télécharger</span>
                        </button>
                      )}
                    </div>

                    <p className="text-xs text-gray-500 mt-3">
                      {fld.hint} • Max {MAX_SIZE_MB} MB
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero Text Section */}
          <div className="border-b pb-6">
            <h3 className="text-xl font-semibold mb-4" style={{ color: '#5C3A21' }}>
              Texte de la page d'accueil
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Titre principal</label>
                <input
                  type="text"
                  value={settings.heroTitle}
                  onChange={(e) => setSettings({ ...settings, heroTitle: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  data-testid="input-hero-title"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Sous-titre</label>
                <input
                  type="text"
                  value={settings.heroSubtitle}
                  onChange={(e) => setSettings({ ...settings, heroSubtitle: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  data-testid="input-hero-subtitle"
                />
              </div>
            </div>
          </div>

          {/* Contact Info Section */}
          <div>
            <h3 className="text-xl font-semibold mb-4" style={{ color: '#5C3A21' }}>
              Informations de contact
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Téléphone</label>
                <input
                  type="text"
                  value={settings.contactPhone}
                  onChange={(e) => setSettings({ ...settings, contactPhone: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  data-testid="input-contact-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Adresse</label>
                <input
                  type="text"
                  value={settings.contactAddress}
                  onChange={(e) => setSettings({ ...settings, contactAddress: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  data-testid="input-contact-address"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Email</label>
                <input
                  type="email"
                  value={settings.contactEmail}
                  onChange={(e) => setSettings({ ...settings, contactEmail: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  data-testid="input-contact-email"
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="pt-6 border-t">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 rounded-lg text-white font-semibold flex items-center space-x-2 disabled:opacity-50"
              style={{ backgroundColor: '#5C3A21' }}
              data-testid="save-settings-btn"
            >
              <Save className="w-5 h-5" />
              <span>{loading ? 'Sauvegarde...' : 'Sauvegarder les paramètres'}</span>
            </button>
          </div>
        </form>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2">Astuces</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Taille maximale : {MAX_SIZE_MB} MB par image</li>
          <li>• Les modifications s'appliquent immédiatement sur tout le site après sauvegarde</li>
          <li>• Cliquez sur "Télécharger" pour récupérer une image existante</li>
          <li>• Pour le logo : fond transparent recommandé (PNG/SVG)</li>
        </ul>
      </div>
    </div>
  );
};

export default SiteSettings;
